import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Save, Edit, FileText, X, CheckCircle, Circle, Maximize2 } from "lucide-react";
import OfferSheetHistory from "@/components/OfferSheetHistory";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateOfferSheetCaches } from "@/lib/offer-sheet-cache";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import { useAnalytics } from "@/hooks/useAnalytics";
import type { OfferSheet, InsertOfferSheet } from "@shared/schema";

export function OfferSheetModal() {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editingSheet, setEditingSheet] = useState<OfferSheet | null>(null);
  const [isExpandedEdit, setIsExpandedEdit] = useState(false);
  const [expandedContent, setExpandedContent] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { offerSheetModalOpen, setOfferSheetModalOpen, currentModule, setCurrentChatId } =
    useStore();
  const { trackOfferSheetCreated } = useAnalytics();

  // Fetch offer sheets
  const { data: offerSheets, isLoading } = useQuery<OfferSheet[]>({
    queryKey: ["/api/offer-sheets"],
  });

  // Fetch active offer sheet
  const { data: activeOfferSheet } = useQuery<OfferSheet | null>({
    queryKey: ["/api/offer-sheets/active"],
  });

  // Create new conversation when switching offer sheets
  const createNewConversationAfterOfferChange = async () => {
    try {
      const newConversation = await apiRequest("POST", "/api/conversations", {
        moduleId: currentModule,
      });
      setCurrentChatId(newConversation.id);
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
    } catch (error) {
      console.error("Failed to create new conversation after offer sheet change:", error);
    }
  };

  // Create offer sheet mutation
  const createMutation = useMutation({
    mutationFn: async (data: InsertOfferSheet) => {
      return await apiRequest("POST", "/api/offer-sheets", data);
    },
    onSuccess: (newSheet) => {
      invalidateOfferSheetCaches(queryClient);
      setIsEditing(false);
      setEditingSheet(null);
      trackOfferSheetCreated(newSheet.title || t("offerSheetModal.untitled"));
      toast({
        title: t("offerSheetModal.toast.created"),
        description: t("offerSheetModal.toast.createdDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("offerSheetModal.toast.retryTitle"),
        description: t("offerSheetModal.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Update offer sheet mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertOfferSheet> }) => {
      return await apiRequest("PUT", `/api/offer-sheets/${id}`, data);
    },
    onSuccess: () => {
      invalidateOfferSheetCaches(queryClient);
      setIsEditing(false);
      setEditingSheet(null);
      toast({
        title: t("offerSheetModal.toast.updated"),
        description: t("offerSheetModal.toast.updatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("offerSheetModal.toast.retryTitle"),
        description: t("offerSheetModal.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Delete offer sheet mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/offer-sheets/${id}`);
    },
    onSuccess: () => {
      invalidateOfferSheetCaches(queryClient);
      toast({
        title: t("offerSheetModal.toast.deleted"),
        description: t("offerSheetModal.toast.deletedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("offerSheetModal.toast.retryTitle"),
        description: t("offerSheetModal.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Activate offer sheet mutation
  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("PUT", `/api/offer-sheets/${id}/activate`);
    },
    onSuccess: () => {
      invalidateOfferSheetCaches(queryClient);

      // Create new conversation when switching offer sheets (if not in offer-builder module)
      if (currentModule !== "offer-builder") {
        createNewConversationAfterOfferChange();
      }

      toast({
        title: t("offerSheetModal.toast.activated"),
        description: t("offerSheetModal.toast.activatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("offerSheetModal.toast.retryTitle"),
        description: t("offerSheetModal.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Deactivate all offer sheets mutation
  const deactivateMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PUT", "/api/offer-sheets/deactivate");
    },
    onSuccess: () => {
      invalidateOfferSheetCaches(queryClient);

      // Create new conversation when deactivating offer sheets (if not in offer-builder module)
      if (currentModule !== "offer-builder") {
        createNewConversationAfterOfferChange();
      }

      toast({
        title: t("offerSheetModal.toast.deactivated"),
        description: t("offerSheetModal.toast.deactivatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("offerSheetModal.toast.retryTitle"),
        description: t("offerSheetModal.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const handleCreateNew = () => {
    setEditingSheet(null);
    setIsEditing(true);
  };

  const handleEdit = (sheet: OfferSheet) => {
    setEditingSheet(sheet);
    setIsEditing(true);
  };

  const handleSave = (formData: FormData) => {
    const data: InsertOfferSheet = {
      title: (formData.get("title") as string) || t("offerSheetModal.defaultTitle"),
      content: (formData.get("content") as string) || "",
    };

    if (editingSheet) {
      updateMutation.mutate({ id: editingSheet.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm(t("offerSheetModal.confirmDelete"))) {
      deleteMutation.mutate(id);
    }
  };

  const handleActivate = (id: string) => {
    // If clicking on already active sheet, deactivate it
    if (activeOfferSheet?.id === id) {
      deactivateMutation.mutate();
    } else {
      activateMutation.mutate(id);
    }
  };

  const closeModal = () => {
    setOfferSheetModalOpen(false);
    setIsEditing(false);
    setEditingSheet(null);
  };

  return (
    <Dialog open={offerSheetModalOpen} onOpenChange={setOfferSheetModalOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("offerSheetModal.title")}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left side - List of offer sheets */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{t("offerSheetModal.yourSheets")}</h3>
                {activeOfferSheet && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("offerSheetModal.active")}:{" "}
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {activeOfferSheet.title}
                    </span>
                  </p>
                )}
              </div>
              <Button onClick={handleCreateNew} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                {t("offerSheetModal.new")}
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : offerSheets?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">{t("offerSheetModal.empty")}</p>
                <p className="text-xs">{t("offerSheetModal.emptyHint")}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {offerSheets?.map((sheet) => (
                  <Card
                    key={sheet.id}
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                      activeOfferSheet?.id === sheet.id
                        ? "ring-2 ring-green-500 bg-green-50 dark:bg-green-950"
                        : ""
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-sm truncate">{sheet.title}</h3>
                            {activeOfferSheet?.id === sheet.id && (
                              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3" />
                                {t("offerSheetModal.activeBadge")}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {sheet.content
                              ? sheet.content.substring(0, 100) + "..."
                              : t("offerSheetModal.noContent")}
                          </p>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button
                            size="sm"
                            variant={activeOfferSheet?.id === sheet.id ? "default" : "ghost"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivate(sheet.id);
                            }}
                            className="h-6 w-6 p-0"
                            title={
                              activeOfferSheet?.id === sheet.id
                                ? t("offerSheetModal.deactivateTitle")
                                : t("offerSheetModal.activateTitle")
                            }
                          >
                            {activeOfferSheet?.id === sheet.id ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <Circle className="h-3 w-3" />
                            )}
                          </Button>
                          <div onClick={(e) => e.stopPropagation()}>
                            <OfferSheetHistory
                              offerSheetId={sheet.id}
                              offerSheetTitle={sheet.title}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(sheet);
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(sheet.id);
                            }}
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Right side - Edit form */}
          <div className="space-y-4">
            {isEditing ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingSheet ? t("offerSheetModal.editTitle") : t("offerSheetModal.newTitle")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSave(new FormData(e.currentTarget));
                    }}
                    className="space-y-4"
                  >
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="title">{t("offerSheetModal.titleLabel")}</Label>
                        <Input
                          id="title"
                          name="title"
                          defaultValue={editingSheet?.title || ""}
                          placeholder={t("offerSheetModal.titlePlaceholder")}
                          required
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="content">{t("offerSheetModal.contentLabel")}</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const currentTextarea = document.getElementById(
                                "content"
                              ) as HTMLTextAreaElement;
                              setExpandedContent(
                                currentTextarea?.value || editingSheet?.content || ""
                              );
                              setIsExpandedEdit(true);
                            }}
                            className="gap-2 h-7 px-2"
                          >
                            <Maximize2 className="h-3 w-3" />
                            {t("offerSheetModal.expandEdit")}
                          </Button>
                        </div>
                        <Textarea
                          id="content"
                          name="content"
                          defaultValue={editingSheet?.content || ""}
                          placeholder={t("offerSheetModal.contentPlaceholder")}
                          rows={15}
                          className="resize-none"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        className="flex-1"
                        disabled={createMutation.isPending || updateMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {t("offerSheetModal.save")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setEditingSheet(null);
                        }}
                      >
                        {t("offerSheetModal.cancel")}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">{t("offerSheetModal.selectToEdit")}</p>
                <p className="text-xs">{t("offerSheetModal.selectHint")}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Expanded Content Edit Modal */}
      <Dialog open={isExpandedEdit} onOpenChange={setIsExpandedEdit}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t("offerSheetModal.expandedEditTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-[80vh]">
            <Textarea
              value={expandedContent}
              onChange={(e) => setExpandedContent(e.target.value)}
              placeholder={t("offerSheetModal.contentPlaceholder")}
              className="flex-1 resize-none text-sm font-mono"
              style={{ minHeight: "60vh" }}
            />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => {
                  // Update the original textarea
                  const originalTextarea = document.getElementById(
                    "content"
                  ) as HTMLTextAreaElement;
                  if (originalTextarea) {
                    originalTextarea.value = expandedContent;
                  }
                  setIsExpandedEdit(false);
                }}
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                {t("offerSheetModal.saveAndClose")}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsExpandedEdit(false)}>
                {t("offerSheetModal.cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
